import { useEffect, useMemo } from 'react';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useToast } from '@chakra-ui/react';
import {
  conversationsByFamilyAtom,
  currentUserAtom,
  familiesAtom,
  updateFamilyAtom,
} from '../store';
import {
  loadPictureKeys,
  myPictureKey,
  pictureForm,
  PictureTooLargeError,
  wrapPictureKey,
} from '../lib/e2ee/pictures';
import {
  setFamilyPicture,
  setGroupPicture,
  setMyPicture,
} from '../lib/pictureSetting';
import {
  e2eeUserId,
  familyKeyEpoch,
  isE2EEReady,
  scopeKeyEpoch,
  startE2EE,
} from '../lib/e2ee/session';
import { ApiError } from '../lib/api';
import { t } from '../i18n';
import { KEYS_ARRIVED_EVENT } from './useE2EE';

// Moving pictures over and keeping picture keys current (M72, D295, D297).
// One pass: my readable picture is sealed; so are the readable pictures of
// families I'm a root or admin of and of groups I'm in (any member, D234);
// then my picture's key is wrapped for each family that has no key for it,
// or one under an older family key. Whatever can't be done now (no key
// here yet, offline) is tried again on the next trigger.

const ADDRESS = /^\/api\/avatars\/([0-9a-f]{32})$/;

/** What a pass works from: read from the store when it starts. */
export interface UpkeepState {
  userId: string;
  avatarUrl: string | null;
  families: Array<{ id: string; role: string; avatarUrl: string | null }>;
  groups: Array<{ id: string; avatarUrl: string | null }>;
}

/** What a pass reports back. */
export interface UpkeepEffects {
  mine(avatarUrl: string | null): void;
  family(familyId: string, avatarUrl: string | null): void;
  group(conversationId: string, avatarUrl: string | null): void;
  /** Tells the person something, once. */
  tell(message: string): void;
}

// Readable pictures already sealed (or that never can be: too big, or
// refused), by address, so each is sealed once (D297).
const moved = new Set<string>();

const lostKey = (userId: string) => `twinden:pictureLost:${userId}`;

function toldLost(userId: string, hash: string): boolean {
  try {
    return localStorage.getItem(lostKey(userId)) === hash;
  } catch {
    return false;
  }
}

function rememberToldLost(userId: string, hash: string) {
  try {
    localStorage.setItem(lostKey(userId), hash);
  } catch {
    // Told again next visit; no harm.
  }
}

/** A readable picture's data URL, or null (sealed, none, or can't tell). */
async function readable(address: string | null): Promise<string | null> {
  if (!address || moved.has(address)) return null;
  const form = await pictureForm(address).catch(() => null);
  return form && 'readable' in form ? form.readable : null;
}

/** Whether my picture is sealed: known from a key row, or fetched once. */
async function mineSealed(
  address: string,
  keyed: boolean
): Promise<{ sealed: boolean; readable: string | null }> {
  if (keyed) return { sealed: true, readable: null };
  // Sealed already, the store not caught up yet.
  if (moved.has(address)) return { sealed: false, readable: null };
  const form = await pictureForm(address).catch(() => null);
  return {
    sealed: !!form && 'sealed' in form,
    readable: form && 'readable' in form ? form.readable : null,
  };
}

const RETRYABLE = new Set(['ROTATION_REQUIRED', 'STALE_KEY', 'STALE_PICTURE']);

function refusedForGood(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status >= 400 &&
    err.status < 500 &&
    // Timed out or rate-limited: the picture is fine, try again (as on
    // Android and iOS).
    err.status !== 408 &&
    err.status !== 429 &&
    !RETRYABLE.has(err.code)
  );
}

async function sealReadable(
  address: string,
  seal: () => Promise<{ data: { avatarUrl: string | null } }>,
  done: (avatarUrl: string | null) => void
): Promise<string | null> {
  try {
    const response = await seal();
    moved.add(address);
    done(response.data.avatarUrl);
    return response.data.avatarUrl;
  } catch (err) {
    // Too big once sealed, or refused for good (not mine to change, gone):
    // not tried again this visit; the D297 sweep has it, and it can be set
    // again by hand. Offline and key races are tried on the next trigger.
    if (err instanceof PictureTooLargeError || refusedForGood(err)) {
      moved.add(address);
    }
    return null;
  }
}

/** The families whose current key is on this device. */
async function withKeyHere(familyIds: string[]): Promise<string[]> {
  const epochs = await Promise.all(familyIds.map((id) => familyKeyEpoch(id)));
  return familyIds.filter((_, index) => epochs[index] !== null);
}

/** One pass of upkeep (see above). Never throws. */
export async function upkeepPictures(
  state: UpkeepState,
  effects: UpkeepEffects
): Promise<void> {
  const { userId } = state;
  // The key session must be this person's, and set up (D55).
  // Checked again before each write: a pass can't be cancelled, so if
  // someone else signs in meanwhile it stops rather than write as them.
  const stillMe = () => e2eeUserId() === userId;
  if (!stillMe()) return;
  await startE2EE(userId);
  if (!isE2EEReady() || !stillMe()) return;
  const familyIds = state.families.map((family) => family.id);
  // Every family's picture keys, shared with the faces on screen (D292).
  await Promise.all(
    familyIds
      .filter((id) => myPictureKey(id) === undefined)
      .map((id) => loadPictureKeys(id))
  );

  // 1. My picture, if it's still readable (D297). A row for its hash
  // somewhere means it's sealed already: no fetch.
  const address = state.avatarUrl;
  const hashOf = (url: string | null) =>
    (url && ADDRESS.exec(url)?.[1]) ?? null;
  const keyed = (hash: string | null) =>
    !!hash && familyIds.some((id) => myPictureKey(id)?.pictureHash === hash);
  // Only a picture known to be sealed has a key to keep current.
  let sealedHash: string | null = null;
  if (address) {
    const mine = await mineSealed(address, keyed(hashOf(address)));
    if (mine.sealed) sealedHash = hashOf(address);
    const dataUrl = mine.readable;
    // Sealed with its keys in the same write: nothing to wrap after. Only
    // for families whose current key is here: sealing for another would
    // start a new family key in the background. None yet: next pass; the
    // rest are wrapped later (step 4).
    if (dataUrl) {
      const ready = await withKeyHere(familyIds);
      if (!stillMe()) return;
      if (ready.length > 0) {
        await sealReadable(
          address,
          () => setMyPicture(userId, dataUrl, ready),
          effects.mine
        );
      }
    }
  }

  // 2. Families I'm a root or admin of (D297).
  for (const family of state.families) {
    if (family.role !== 'root' && family.role !== 'admin') continue;
    const dataUrl = await readable(family.avatarUrl);
    if (!dataUrl) continue;
    // Its key not here yet: sealing would start a new one.
    if ((await familyKeyEpoch(family.id)) === null) continue;
    if (!stillMe()) return;
    await sealReadable(
      family.avatarUrl!,
      () => setFamilyPicture(family.id, dataUrl),
      (url) => effects.family(family.id, url)
    );
  }

  // 3. Groups I'm in: any member may (D234).
  for (const group of state.groups) {
    const dataUrl = await readable(group.avatarUrl);
    if (!dataUrl) continue;
    // The chat's current key not here yet: likewise.
    if ((await scopeKeyEpoch('chat', group.id)) === null) continue;
    if (!stillMe()) return;
    await sealReadable(
      group.avatarUrl!,
      () => setGroupPicture(group.id, dataUrl),
      (url) => effects.group(group.id, url)
    );
  }

  // 4. My picture's key, for each family without one for it, or with one
  // under an older family key (D295). Readable, or can't tell: nothing to
  // wrap.
  const hash = sealedHash;
  if (!hash) return;
  let wrappedAny = false;
  for (const familyId of familyIds) {
    const row = myPictureKey(familyId);
    if (row === undefined) continue;
    try {
      // Only with the family's current key here: without it the write
      // would start a new family key rather than wait for this one.
      const epoch = await familyKeyEpoch(familyId);
      if (epoch === null) continue;
      if (row && row.pictureHash === hash && row.epoch >= epoch) continue;
      if (!stillMe()) return;
      if (await wrapPictureKey(familyId, userId, hash)) wrappedAny = true;
    } catch {
      // No family key here now, offline, or the picture moved on
      // (STALE_PICTURE): the next pass tries again.
    }
  }

  // 5. A picture no family holds a key for can't be opened by anyone, and
  // its key isn't on this device either (it lives in memory only): tell
  // the person once to set it again; initials show meanwhile. Read again
  // first, so a picture just set elsewhere isn't taken for a lost one.
  if (wrappedAny || keyed(hash) || familyIds.length === 0) return;
  // A row of mine for another picture: it moved on elsewhere, and this
  // store hasn't caught up. Not lost.
  const movedOn = () =>
    familyIds.some((id) => {
      const row = myPictureKey(id);
      return !!row && row.pictureHash !== hash;
    });
  if (movedOn()) return;
  if (toldLost(userId, hash)) return;
  await Promise.all(
    familyIds.map((id) => loadPictureKeys(id, { fresh: true }))
  );
  if (familyIds.some((id) => myPictureKey(id) === undefined)) return;
  if (keyed(hash) || movedOn() || !stillMe()) return;
  rememberToldLost(userId, hash);
  effects.tell(t('e2ee.pictureLost'));
}

/**
 * Runs `pass` one at a time: a trigger while one runs asks for one more
 * pass after it (however many triggers came), not one each.
 */
export function oneAtATime(pass: () => Promise<void>): () => Promise<void> {
  let running: Promise<void> | null = null;
  let again = false;
  return () => {
    if (running) {
      again = true;
      return running;
    }
    running = (async () => {
      do {
        again = false;
        await pass().catch(() => undefined);
      } while (again);
      // Cleared in the same turn as the last check of `again`, so a
      // trigger can't land between them and be lost.
      running = null;
    })();
    return running;
  };
}

function readState(): UpkeepState | null {
  const store = getDefaultStore();
  const user = store.get(currentUserAtom);
  if (!user) return null;
  const groups = Object.values(store.get(conversationsByFamilyAtom))
    .flat()
    .filter((chat) => chat.type === 'group')
    .map((chat) => ({ id: chat.id, avatarUrl: chat.avatarUrl }));
  return {
    userId: user.id,
    avatarUrl: user.avatarUrl ?? null,
    families: store
      .get(familiesAtom)
      .map(({ id, role, avatarUrl }) => ({ id, role, avatarUrl })),
    groups,
  };
}

/**
 * Keeps pictures sealed and their keys current (D295, D297): on signing
 * in, when the families (or their chat lists) load or change, and when
 * keys arrive. Mounted once, in the app shell.
 */
export function usePictureUpkeep() {
  const user = useAtomValue(currentUserAtom);
  const families = useAtomValue(familiesAtom);
  const chats = useAtomValue(conversationsByFamilyAtom);
  const toast = useToast();

  const run = useMemo(() => {
    const effects: UpkeepEffects = {
      mine: (avatarUrl) => {
        const store = getDefaultStore();
        const current = store.get(currentUserAtom);
        if (current) store.set(currentUserAtom, { ...current, avatarUrl });
      },
      family: (id, avatarUrl) =>
        getDefaultStore().set(updateFamilyAtom, {
          id,
          updates: { avatarUrl },
        }),
      group: (id, avatarUrl) => {
        const store = getDefaultStore();
        const lists = store.get(conversationsByFamilyAtom);
        store.set(
          conversationsByFamilyAtom,
          Object.fromEntries(
            Object.entries(lists).map(([familyId, list]) => [
              familyId,
              list.map((chat) =>
                chat.id === id ? { ...chat, avatarUrl } : chat
              ),
            ])
          )
        );
      },
      tell: (message) =>
        toast({ title: message, status: 'info', duration: 10000 }),
    };
    return oneAtATime(async () => {
      const state = readState();
      if (state) await upkeepPictures(state, effects);
    });
  }, [toast]);

  // What a pass looks at: who I am and my picture, my families with my
  // role and their pictures, and my groups' pictures.
  const userId = user?.id ?? null;
  const avatarUrl = user?.avatarUrl ?? null;
  const familiesKey = families
    .map((family) => `${family.id}:${family.role}:${family.avatarUrl ?? ''}`)
    .sort()
    .join(',');
  const groupsKey = Object.values(chats)
    .flat()
    .filter((chat) => chat.type === 'group' && chat.avatarUrl)
    .map((chat) => `${chat.id}:${chat.avatarUrl}`)
    .sort()
    .join(',');

  useEffect(() => {
    if (userId) void run();
  }, [run, userId, avatarUrl, familiesKey, groupsKey]);

  // A chat's or family's keys arrived (`keys:shared`, or an ask learned
  // they came): what couldn't be done may be done now.
  useEffect(() => {
    if (!userId) return;
    const onKeys = () => void run();
    window.addEventListener(KEYS_ARRIVED_EVENT, onKeys);
    return () => window.removeEventListener(KEYS_ARRIVED_EVENT, onKeys);
  }, [run, userId]);
}
